import { DataType, InverterData, InverterDataComposer, ReadWrite, RegisterTable } from '../lib/inverter.model';
import {
  createFault,
  createTemperature,
  create_bat_config,
  V3RealTimeConfig,
  V3InternalEnergyConfig,
  create_V_C_P_pv,
} from './inverter.config';

export enum OperationStatus {
  Waiting = 0,
  Detection = 1,
  GridConnected = 2,
  EmergencyPowerSupply = 3,
  RecoverableFault = 4,
  PermanentFault = 5,
  Upgrade = 6,
  SelfCharging = 7,
  SVG = 8,
  PID = 9,
}

export type OperationStatusConfig = Record<OperationStatus, {
  label: string;
  classes: string;
}>;

export const OperationStatusConfig: OperationStatusConfig = {
  [OperationStatus.Waiting]: {
    label: 'Waiting',
    classes: 'bg-grey-100',
  },
  [OperationStatus.Detection]: {
    label: 'Detection',
    classes: 'bg-grey-100',
  },
  [OperationStatus.GridConnected]: {
    label: 'Grid Connected',
    classes: 'bg-green-100',
  },
  [OperationStatus.EmergencyPowerSupply]: {
    label: 'Emergency Power Supply',
    classes: 'bg-yellow-100',
  },
  [OperationStatus.RecoverableFault]: {
    label: 'Recoverable Fault',
    classes: 'bg-yellow-100',
  },
  [OperationStatus.PermanentFault]: {
    label: 'Permanent Fault',
    classes: 'bg-red-100',
  },
  [OperationStatus.Upgrade]: {
    label: 'Upgrade',
    classes: 'bg-grey-100',
  },
  [OperationStatus.SelfCharging]: {
    label: 'Self Charging',
    classes: 'bg-grey-100',
  },
  [OperationStatus.SVG]: {
    label: 'SVG',
    classes: 'bg-grey-100',
  },
  [OperationStatus.PID]: {
    label: 'PID',
    classes: 'bg-grey-100',
  },
};

export const V3RealTimeDataByRegister: Partial<RegisterTable> = {
  SysState:   {
    address: 0x0404,
    name: 'SysState',
    dataType: DataType.U16,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: `
    Operating status
      0: waiting state
      1: Detection status
      2: Grid-connected status
      3: Emergency power supply status
      4: Recoverable fault state
      5: Permanent fault status
      6: Upgrade status
      7: Self-charging status
      8：SVG status
      9：PID  status`,
    quantity: 1,
  },
  Countdown: {
    address: 0x0417,
    name: 'Countdown',
    dataType: DataType.U16,
    unit: 's',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Power-on countdown',
    quantity: 1,
  },
  GenerationTime_Today: {
    address: 0x0426,
    name: 'GenerationTime_Today',
    dataType: DataType.U16,
    unit: 'min',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Day of power generation time',
    quantity: 1,
  },
  GenerationTime_Total: {
    address: 0x0427,
    name: 'GenerationTime_Total',
    dataType: DataType.U32,
    unit: 'min',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Total power generation time',
    quantity: 2,
  },
  ServiceTime_Total: {
    address: 0x0429,
    name: 'ServiceTime_Total',
    dataType: DataType.U32,
    unit: 'min',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Total service time',
    quantity: 2,
  },
  Insulation_Resistance: {
    address: 0x042b,
    name: 'Insulation_Resistance',
    dataType: DataType.U16,
    unit: 'kΩ',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Insulation resistance',
    quantity: 1,
  },
  SysTime_Year: {
    address: 0x042c,
    name: 'SysTime_Year',
    dataType: DataType.U16,
    unit: 'year',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time year',
    quantity: 1,
  },
  SysTime_Month: {
    address: 0x042d,
    name: 'SysTime_Month',
    dataType: DataType.U16,
    unit: 'month',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time month',
    quantity: 1,
  },
  SysTime_Day: {
    address: 0x042e,
    name: 'SysTime_Day',
    dataType: DataType.U16,
    unit: 'day',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time day',
    quantity: 1,
  },
  SysTime_Hour: {
    address: 0x042f,
    name: 'SysTime_Hour',
    dataType: DataType.U16,
    unit: 'hour',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time hour',
    quantity: 1,
  },
  SysTime_Minute: {
    address: 0x0430,
    name: 'SysTime_Minute',
    dataType: DataType.U16,
    unit: 'minute',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time minute',
    quantity: 1,
  },
  SysTime_Second: {
    address: 0x0431,
    name: 'SysTime_Second',
    dataType: DataType.U16,
    unit: 'second',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'System time second',
    quantity: 1,
  },
};

const fault_first_section_start = V3RealTimeConfig.Fault_first_section.start;
const fault_first_section_index_start = V3RealTimeConfig.Fault_first_section.indexToStart;
for (let i = 0; i < V3RealTimeConfig.Fault_first_section.length; i++) {
  V3RealTimeDataByRegister[`Fault${fault_first_section_index_start + i}`] = createFault(
    fault_first_section_index_start + i,
    fault_first_section_start + i,
  ).fault;
}

const fault_second_section_start = V3RealTimeConfig.Fault_second_section.start;
const fault_second_section_index_start = V3RealTimeConfig.Fault_second_section.indexToStart;
for (let i = 0; i < V3RealTimeConfig.Fault_second_section.length; i++) {
  V3RealTimeDataByRegister[`Fault${fault_second_section_index_start + i}`] = createFault(
    fault_second_section_index_start + i,
    fault_second_section_start + i,
  ).fault;
}

const temperature_env_start = V3RealTimeConfig.Temperature_env.start;
for (let i = 0; i < V3RealTimeConfig.Temperature_env.length; i++) {
  V3RealTimeDataByRegister[`Temperature_${V3RealTimeConfig.Temperature_env.post}${i + 1}`] = createTemperature(
    i + 1,
    temperature_env_start + i,
    V3RealTimeConfig.Temperature_env.post,
  ).temperature;
}

const temperature_heatSink_start = V3RealTimeConfig.Temperature_heatSink.start;
for (let i = 0; i < V3RealTimeConfig.Temperature_heatSink.length; i++) {
  V3RealTimeDataByRegister[`Temperature_${V3RealTimeConfig.Temperature_heatSink.post}${i + 1}`] = createTemperature(
    i + 1,
    temperature_heatSink_start + i,
    V3RealTimeConfig.Temperature_heatSink.post,
  ).temperature;
}

const temperature_inv_start = V3RealTimeConfig.Temperature_inv.start;
for (let i = 0; i < V3RealTimeConfig.Temperature_inv.length; i++) {
  V3RealTimeDataByRegister[`Temperature_${V3RealTimeConfig.Temperature_inv.post}${i + 1}`] = createTemperature(
    i + 1,
    temperature_inv_start + i,
    V3RealTimeConfig.Temperature_inv.post,
  ).temperature;
}

export const V3InternalEnergy: Partial<RegisterTable> = {
  ActivePower_Output_R: {
    address: 0x048f,
    name: 'ActivePower_Output_R',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'The R-phase inverter outputs active power. Discharge is positive, charge is negative',
    quantity: 1,
  },
  Voltage_Phase_R: {
    address: 0x048d,
    name: 'Voltage_Phase_R',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'R phase grid voltage',
    quantity: 1,
  },
  Current_Output_R: {
    address: 0x048e,
    name: 'Current_Output_R',
    dataType: DataType.U16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'R-phase inverter output current',
    quantity: 1,
  },
  ActivePower_Output_S: {
    address: 0x049a,
    name: 'ActivePower_Output_S',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'The S-phase inverter outputs active power. Discharge is positive, charge is negative',
    quantity: 1,
  },
  Voltage_Phase_S: {
    address: 0x0498,
    name: 'Voltage_Phase_S',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'S phase grid voltage',
    quantity: 1,
  },
  Current_Output_S: {
    address: 0x0499,
    name: 'Current_Output_S',
    dataType: DataType.U16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'S-phase inverter output current',
    quantity: 1,
  },
  ActivePower_Output_T: {
    address: 0x04a5,
    name: 'ActivePower_Output_T',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'The T-phase inverter outputs active power. Discharge is positive, charge is negative',
    quantity: 1,
  },
  Voltage_Phase_T: {
    address: 0x04a3,
    name: 'Voltage_Phase_T',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'T phase grid voltage',
    quantity: 1,
  },
  Current_Output_T: {
    address: 0x04a4,
    name: 'Current_Output_T',
    dataType: DataType.U16,
    unit: 'A',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'T-phase inverter output current',
    quantity: 1,
  },
  AddressMask_Realtime_Input_PV1: {
    address: 0x0580,
    name: 'AddressMask_Realtime_Input_PV1',
    dataType: DataType.U64,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Address Mask Realtime Input PV1',
    quantity: 4,
    isMask: true,
  },
};
const V_C_P_pv_Start = V3InternalEnergyConfig.V_C_P_pv.start;
for (let i = 1; i <= V3InternalEnergyConfig.V_C_P_pv.length; i++) {
  const index = JSON.parse(JSON.stringify(i));
  const { v_pv, c_pv, p_pv } = create_V_C_P_pv(
    i,
    V_C_P_pv_Start + (index - 1) * 3,
    V_C_P_pv_Start + (index - 1) * 3 + 1,
    V_C_P_pv_Start + (index - 1) * 3 + 2,
  );
  V3InternalEnergy[`v_pv${index}`] = v_pv;
  V3InternalEnergy[`c_pv${index}`] = c_pv;
  V3InternalEnergy[`p_pv${index}`] = p_pv;
}

export const V3EnergyAttributesByRegister: Partial<RegisterTable> = {
  EG1: {
    address: 0x0684,
    name: 'PV_Generation_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'PV generation today',
    quantity: 2,
  },
  ET1: {
    address: 0x0686,
    name: 'PV_Generation_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total power generation time',
    quantity: 2,
  },
  EG4: {
    address: 0x0688,
    name: 'Load_Consumption_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Load power consumption of the day',
    quantity: 2,
  },
  ET4: {
    address: 0x068a,
    name: 'Load_Consumption_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total load power consumption',
    quantity: 2,
  },
  EG3: {
    address: 0x068c,
    name: 'Energy_Purchase_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Buy electricity on the day',
    quantity: 2,
  },
  ET3: {
    address: 0x068e,
    name: 'Energy_Purchase_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total electricity purchased',
    quantity: 2,
  },
  EG2: {
    address: 0x0690,
    name: 'Energy_Selling_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Electricity sold on the day',
    quantity: 2,
  },
  ET2: {
    address: 0x0692,
    name: 'Energy_Selling_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total electricity sold',
    quantity: 2,
  },
  EG5: {
    address: 0x0694,
    name: 'Bat_Charge_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Battery charge capacity of the day',
    quantity: 2,
  },
  ET5: {
    address: 0x0696,
    name: 'Bat_Charge_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total battery charge capacity',
    quantity: 2,
  },
  EG6: {
    address: 0x0698,
    name: 'Bat_Discharge_Today',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Battery discharge capacity of the day',
    quantity: 2,
  },
  ET6: {
    address: 0x069a,
    name: 'Bat_Discharge_Total',
    dataType: DataType.U32,
    unit: 'kWh',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total battery discharge capacity',
    quantity: 2,
  },
};

export const V3PowerAttributeByRegister: Partial<RegisterTable> = {
  AddressMask_Realtime_Input_PV2: {
    address: 0x05c0,
    name: 'AddressMask_Realtime_Input_PV2',
    dataType: DataType.U64,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Address mask of PV2',
    quantity: 4,
    isMask: true,
  },
  P1 :{
    address: 0x05c4,
    name: 'Power_PV_Total',
    dataType: DataType.U16,
    unit: 'kW',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total PV power',
    quantity: 1,
  },
  P2: {
    address: 0x0488,
    name: 'ActivePower_PCC_Total',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Total PCC active power. Selling electricity is positive, buying electricity is negative',
    quantity: 1,
  },
  P4: {
    address: 0x04af,
    name: 'ActivePower_Load_Sys',
    dataType: DataType.U16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'System total load power',
    quantity: 1,
  },
  P5: {
    address: 0x0667,
    name: 'Power_Bat_Total',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'Total battery charge and discharge power. Charge is positive and discharge is negative',
    quantity: 1,
  },
  P9: {
    address: 0x04ae,
    name: 'ActivePower_PV_Ext',
    dataType: DataType.U16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'External power generation',
    quantity: 1,
  },
  BP: {
    address: 0x0608,
    name: 'SOC_Bat_Average',
    dataType: DataType.U16,
    unit: '%',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Summarize the average value of battery pack SOC',
    quantity: 1,
  },
};

export const V3BatteryAttributeByRegister: Partial<RegisterTable> = {
  AddressMask_Realtime_Input_Bat1: {
    address: 0x0600,
    name: 'AddressMask_Realtime_Input_Bat1',
    dataType: DataType.U64,
    unit: '',
    accuracy: 1,
    access: ReadWrite.Read,
    description: 'Address mask of battery 1',
    quantity: 4,
    isMask: true,
  },
};
const batteries_first_section_start = V3RealTimeConfig.Batteries_first_section.start;
const batteries_first_section_index_start = V3RealTimeConfig.Batteries_first_section.indexToStart;
for (let i = 1; i < V3RealTimeConfig.Batteries_first_section.length; i++) {
  const { v_bat, c_bat, p_bat, t_env_bat, soc_bat, soh_bat, chargeCycle_bat } = create_bat_config(
    batteries_first_section_index_start + i,
    batteries_first_section_start + (i - 1) * 7,
  );
  V3BatteryAttributeByRegister[`V_Bat${batteries_first_section_index_start + (i - 1)}`] = v_bat;
  V3BatteryAttributeByRegister[`C_Bat${batteries_first_section_index_start + (i - 1)}`] = c_bat;
  V3BatteryAttributeByRegister[`P_Bat${batteries_first_section_index_start + (i - 1)}`] = p_bat;
  V3BatteryAttributeByRegister[`T_Env_Bat${batteries_first_section_index_start + (i - 1)}`] = t_env_bat;
  V3BatteryAttributeByRegister[`SOC_Bat${batteries_first_section_index_start + (i - 1)}`] = soc_bat;
  V3BatteryAttributeByRegister[`SOH_Bat${batteries_first_section_index_start + (i - 1)}`] = soh_bat;
  V3BatteryAttributeByRegister[`ChargeCycle_Bat${batteries_first_section_index_start + (i - 1)}`] = chargeCycle_bat;
}

/**
 * @abstract It is assumed that the number of batteries is less than
 * that of the first section so it is not necessary to request the second section
 * (this would cost sending another command)
 */
/**
  const batteries_second_section_start = V3RealTimeConfig.Batteries_second_section.start;
  const batteries_second_section_index_start = V3RealTimeConfig.Batteries_second_section.indexToStart;
  for (let i = 0; i < V3RealTimeConfig.Batteries_second_section.length; i++) {
    const { v_bat, c_bat, p_bat, t_env_bat, soc_bat, soh_bat, chargeCycle_bat } = create_bat_config(
      batteries_second_section_index_start + i,
      batteries_second_section_start + i,
    );
    V3BatteryAttributeByRegister[`V_Bat${batteries_second_section_index_start + i}`] = v_bat;
    V3BatteryAttributeByRegister[`C_Bat${batteries_second_section_index_start + i}`] = c_bat;
    V3BatteryAttributeByRegister[`P_Bat${batteries_second_section_index_start + i}`] = p_bat;
    V3BatteryAttributeByRegister[`T_Env_Bat${batteries_second_section_index_start + i}`] = t_env_bat;
    V3BatteryAttributeByRegister[`SOC_Bat${batteries_second_section_index_start + i}`] = soc_bat;
    V3BatteryAttributeByRegister[`SOH_Bat${batteries_second_section_index_start + i}`] = soh_bat;
    V3BatteryAttributeByRegister[`ChargeCycle_Bat${batteries_second_section_index_start + i}`] = chargeCycle_bat;
  }
 */

export const V3DeviceGridConfig: Partial<RegisterTable> = {
  Frequency_Grid: {
    address: 0x0484,
    name: 'Frequency_Grid',
    dataType: DataType.U16,
    unit: 'Hz',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'Grid frequency',
    quantity: 1,
  },
  ActivePower_PCC_R: {
    address: 0x0493,
    name: 'ActivePower_PCC_R',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'R-phase PCC active power',
    quantity: 1,
  },
  ActivePower_PCC_S: {
    address: 0x049e,
    name: 'ActivePower_PCC_S',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'S-phase PCC active power. Discharge is positive, charge is negative',
    quantity: 1,
  },
  ActivePower_PCC_T: {
    address: 0x04a9,
    name: 'ActivePower_PCC_T',
    dataType: DataType.I16,
    unit: 'kW',
    accuracy: 0.01,
    access: ReadWrite.Read,
    description: 'T-phase PCC active power. Discharge is positive, charge is negative',
    quantity: 1,
  },
  Voltage_Phase_R: {
    address: 0x048d,
    name: 'Voltage_Phase_R',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'R phase grid voltage',
    quantity: 1,
  },
  Voltage_Phase_S: {
    address: 0x0498,
    name: 'Voltage_Phase_S',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'S phase grid voltage',
    quantity: 1,
  },
  Voltage_Phase_T: {
    address: 0x04a3,
    name: 'Voltage_Phase_T',
    dataType: DataType.U16,
    unit: 'V',
    accuracy: 0.1,
    access: ReadWrite.Read,
    description: 'T phase grid voltage',
    quantity: 1,
  },
};

export const ComposeV3RealTimeConfig: Partial<InverterDataComposer> = {
  SysState: (data: Partial<InverterData>) => {
    if (data.SysState && typeof data.SysState === 'number') {
      data.SysState = OperationStatusConfig[data.SysState as OperationStatus].label;
    }
    return data;
  },
};

export const ComposeV3Attributes: Partial<InverterDataComposer> = {
  P3: (data: Partial<InverterData>) => {
    if (data.P2 && typeof data.P2 === 'number' && data.P2 < 0) {
      data.P3 = data.P2 * -1;
      data.P2 = 0;
    }
    return data;
  },
  P6: (data: Partial<InverterData>) => {
    if (data.P5 && typeof data.P5 === 'number' && data.P5 < 0) {
      data.P6 = data.P5 * -1;
      data.P5 = 0;
    }
    return data;
  },
  P1: (data: Partial<InverterData>) => {
    /**
     * @abstract If the value of P1 is 0 -> is necessary sum all strings
     */
    if (data.P1 === 0) {
      data.P1 = 0;
      /**
       * @important V3InternalEnergy table is necessary to sum all strings
       */
      for (let i = 1; i <= V3InternalEnergyConfig.V_C_P_pv.length; i++) {
        data.P1 += (data[`p_pv${i}`] as number) || 0;
      }
    }
    return data;
  },
};
